// src/frontend/Adviser/References/GetAdviserReferences.jsx
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Sidebar from "../../../DesignSystem/Sidebar";
import "../../../../css/Adviser/Adviser.css";
import { useAdviserContext } from "../../../../context/Adviser/AdviserContext";
import { useAdviserActions } from "../../../../context/Adviser/Hooks/useAdviserActions";
import dayjs from "dayjs";
import InputField from "../../../DesignSystem/InputField";
import TableInputField from "../../../DesignSystem/TableInputFields";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronCircleDown,
  faChevronCircleUp,
  faPlus,
  faPlusCircle,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { collection, getDocs } from "firebase/firestore";
import { ReactComponent as CloseIcon } from "../../../../assets/icons/close.svg";
import ButtonGreen from "../../../DesignSystem/Buttons/ButtonGreen";
import EmailPreviewModal from "../../HelperFunctions/EmailPreviewer";
import ButtonYellow from "../../../DesignSystem/Buttons/ButtonYellow";
import ReferencesConsentForm from "./Components/PrintConsentForm";
import { pdf } from "@react-pdf/renderer";
import { db } from "../../../../backend/firebaseConfig";
import { useFormUtils } from "../../../../context/Adviser/Utils/FormUtils";
import { saveAdviserData } from "../../../../context/Adviser/SaveAdviser";
import WorkHistoryHeatmap from "../../Charts/WorkHistoryHeatMap";

// --- tiny date helpers (Firestore Timestamp-safe)
const tsToDate = (v) =>
  v?.toDate?.() ? v.toDate() : v?.seconds ? new Date(v.seconds * 1000) : v;
const fmtMonth = (v) => (v ? dayjs(tsToDate(v)).format("YYYY-MM") : "");
const fmtDate = (v, f = "DD/MM/YYYY") =>
  v ? dayjs(tsToDate(v)).format(f) : "";

const removeAtIndex = (arr = [], idx) => arr.filter((_, i) => i !== idx);

const GetAdviserReferences = () => {
  const { docId } = useParams();
  const { state, dispatch } = useAdviserContext();
  const { loadAdviser } = useAdviserActions();
  const { gv, hc, hcr } = useFormUtils();

  // pull from the new shape
  const data = state?.data || {};
  const personalDetails = data.personalDetails || {};
  const applicationDetails = data.applicationDetails || {};
  const accountantDetails = data.accountantDetails || {};
  const workHistoryRaw = data.workHistory || [];

  // normalize work history (array or object)
  const workHistory = Array.isArray(workHistoryRaw)
    ? workHistoryRaw
    : Object.values(workHistoryRaw || {});

  // ui state
  const [filter, setFilter] = useState("Work History");
  const [showJobModal, setShowJobModal] = useState(false);
  const [selectedJobForModal, setSelectedJobForModal] = useState(null);
  const [selectedPersonalRefIndex, setSelectedPersonalRefIndex] =
    useState(null);
  const [selectedEducationalRefIndex, setSelectedEducationalRefIndex] =
    useState(null);
  const [expandedRows, setExpandedRows] = useState([]);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailPreview, setEmailPreview] = useState("");

  // refs data
  const [loadingReferences, setLoadingReferences] = useState(true);
  const [nonRegulatedReferences, setNonRegulatedReferences] = useState([]);
  const [regulatedReferences, setRegulatedReferences] = useState([]);
  const [accountantReferences, setAccountantReferences] = useState([]);
  const [personalReferences, setPersonalReferences] = useState([]);
  const [educationalReferences, setEducationalReferences] = useState([]);
  const [selectedReferenceForModal, setSelectedReferenceForModal] =
    useState(null);

  // google form links
  const regulatedReferenceGoogleFormLink =
    "https://forms.gle/di2wAXD5UE5W2FTf7";
  const nonRegulatedReferenceGoogleFormLink =
    "https://forms.gle/JpMG1USNPMaSLDQ69";
  const accountantReferenceGoogleFormLink =
    "https://forms.gle/NkBdS9DgkRXUgM37A";
  const personalReferenceGoogleFormLink = "https://forms.gle/TKjpmfkCbVcMT2LKA";
  const educationalReferenceGoogleFormLink =
    "https://forms.gle/y5ZNsVdmPuHMKuWi7";

  const toggleRow = (i) =>
    setExpandedRows((prev) =>
      prev.includes(i) ? prev.filter((x) => x !== i) : [...prev, i]
    );

  // fetch references (public collections)
  useEffect(() => {
    (async () => {
      setLoadingReferences(true);
      const q = async (collPath) => {
        const snap = await getDocs(collection(db, ...collPath));
        return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      };
      setNonRegulatedReferences(
        await q(["References", "Non Regulated Reference Requests", "Data"])
      );
      setRegulatedReferences(
        await q(["References", "Regulated Reference Requests", "Data"])
      );
      setAccountantReferences(
        await q(["References", "Accountant Reference Requests", "Data"])
      );
      setPersonalReferences(
        await q(["References", "Personal Reference Requests", "Data"])
      );
      setEducationalReferences(
        await q(["References", "Educational Reference Requests", "Data"])
      );
      setLoadingReferences(false);
    })();
  }, [docId]);

  // load single adviser
  useEffect(() => {
    loadAdviser(docId);
  }, [docId]);

  const handleUpdateEmail = (index, updatedHTML) => {
    switch (filter) {
      case "Work History":
        setNonRegulatedReferences((prev) => {
          const updated = [...prev];
          updated[index].body = updatedHTML;
          return updated;
        });
        break;

      case "Accountant Reference":
        setAccountantReferences((prev) => {
          const updated = [...prev];
          updated[index].body = updatedHTML;
          return updated;
        });
        break;

      case "Personal Reference":
        setPersonalReferences((prev) => {
          const updated = [...prev];
          updated[index].body = updatedHTML;
          return updated;
        });
        break;

      case "Educational Reference":
        setEducationalReferences((prev) => {
          const updated = [...prev];
          updated[index].body = updatedHTML;
          return updated;
        });
        break;

      case "Regulated Reference": // If you're separating these from non-regulated
        setRegulatedReferences((prev) => {
          const updated = [...prev];
          updated[index].body = updatedHTML;
          return updated;
        });
        break;

      default:
        // console.warn(`Unknown filter type: ${filter}`);
        break;
    }
  };

  const handleDeleteWorkRef = (
    jobIndex /*, refIndex = 0 because max one */
  ) => {
    if (!window.confirm("Delete this work history reference?")) return;

    const next = [...workHistory];
    const job = { ...next[jobIndex] };

    // remove the key instead of setting undefined
    const { references, ...rest } = job;
    next[jobIndex] = rest; // no `references` field at all

    // optional: collapse the row
    setExpandedRows((prev) => prev.filter((i) => i !== jobIndex));

    dispatch({
      type: "PATCH_SECTION",
      payload: { section: "workHistory", patch: next },
    });
  };

  const handleDeleteAccountantRef = () => {
    dispatch({
      type: "PATCH_SECTION",
      payload: {
        section: "accountantDetails",
        patch: { references: undefined },
      },
    });
  };

  const handleDeletePersonalRef = (refereeIndex, refIndex) => {
    const cur = Array.isArray(accountantDetails?.personalReferees)
      ? accountantDetails.personalReferees
      : [];

    const target = { ...(cur[refereeIndex] || {}) };
    const refs = Array.isArray(target.references) ? target.references : [];
    const newRefs = removeAtIndex(refs, refIndex);

    const next = [...cur];
    next[refereeIndex] = {
      ...target,
      references: newRefs.length ? newRefs : undefined,
    };

    dispatch({
      type: "PATCH_SECTION",
      payload: {
        section: "accountantDetails",
        patch: { personalReferees: next },
      },
    });
  };

  const handleDeleteEducationalRef = (refereeIndex, refIndex) => {
    const cur = Array.isArray(accountantDetails?.educationalReferees)
      ? accountantDetails.educationalReferees
      : [];

    const target = { ...(cur[refereeIndex] || {}) };
    const refs = Array.isArray(target.references) ? target.references : [];
    const newRefs = removeAtIndex(refs, refIndex);

    const next = [...cur];
    next[refereeIndex] = {
      ...target,
      references: newRefs.length ? newRefs : undefined,
    };

    dispatch({
      type: "PATCH_SECTION",
      payload: {
        section: "accountantDetails",
        patch: { educationalReferees: next },
      },
    });
  };

  // EMAIL PREVIEWS (unchanged logic, use helpers where needed)
  const accountantReferenceEmailPreviews = [
    {
      subject: "Accountant Reference Request",
      name: `${gv(accountantDetails?.initials) || ""} ${
        gv(accountantDetails?.surname) || ""
      }`,
      emailAddress: gv(accountantDetails?.emailAddress),
      body: `
        <p>I have been given your details from ${gv(
          personalDetails?.forename
        )} ${gv(
        personalDetails?.surname
      )} in regards to a reference covering dates
        ${fmtMonth(accountantDetails?.from)} - ${dayjs().format("MM/YYYY")}.</p>

        <p>To submit the reference, please complete the Google Form using the link provided below:</p>
        <a href="${accountantReferenceGoogleFormLink}" style="color:#081e56" target="_blank" rel="noopener noreferrer">Submit Reference</a>
        <p>If you have any questions do not hesitate to contact me, I look forward to hearing from you soon.</p>
      `,
    },
  ];

  const personalReferenceEmailPreviews =
    accountantDetails?.personalReferees
      ?.filter(
        (referee) =>
          !Array.isArray(referee?.references) || referee.references.length === 0
      )
      ?.map((referee) => ({
        subject: "Personal Reference Request",
        name: referee.refereeName,
        emailAddress: referee.refereeEmailAddress,
        body: `
          <p>This is a personal reference for one our recruits, ${gv(
            personalDetails?.forename
          )} ${gv(personalDetails?.surname)}.</p>
          <p>I have been given your details from ${gv(
            personalDetails?.forename
          )} ${gv(
          personalDetails?.surname
        )} in regards to a reference covering dates:
          ${workHistory
            .map((job) => {
              const from = fmtMonth(job?.from);
              const to = fmtMonth(job?.to);
              return `<p>${from} - ${to}</p>`;
            })
            .join("")}
          </p>
          <p>To submit the reference, please complete the Google Form using the link provided below:</p>
          <a href="${personalReferenceGoogleFormLink}" style="color:#081e56" target="_blank" rel="noopener noreferrer">Submit Reference</a>
          <br/><br/>
          <p>If you have any queries do not hesitate to contact me, I look forward to hearing from you soon.</p>
        `,
      })) || [];

  const educationalReferenceEmailPreviews =
    accountantDetails?.educationalReferees?.map((referee) => ({
      subject: "Educational Reference Request",
      name: referee.recipientName || "",
      emailAddress: referee.recipientEmail || "",
      body: `
        <p>I am writing to request a reference for ${gv(
          personalDetails?.forename
        )} ${gv(personalDetails?.surname)}, a former student of ${
        referee.educationalInstitute || "your institution"
      }.</p>
        <p>Please submit via the Google Form below:</p>
        <a href="${educationalReferenceGoogleFormLink}" style="color:#081e56" target="_blank" rel="noopener noreferrer">Submit Reference</a>
        <br/><br/>
        <p>Thank you for your time and assistance. I look forward to hearing back from you soon.</p>
      `,
    })) || [];

  const workHistoryEmailPreviews = workHistory
    .filter((job) => dayjs().diff(dayjs(tsToDate(job.from)), "years") <= 6)
    .filter((job) => !job?.references || job.references.length === 0)
    .filter((job) => job?.contactForReference === true)
    .map((job) => {
      const name = job?.nameOfFirm || "";
      const emailAddress = job?.emailAddress || "";
      const subject = job?.regulatedRole
        ? "Regulated Reference Request"
        : "Non-Regulated Reference Request";
      const body = `
        <p>Please see below the details for the individual:</p>
        <ul>
          <li>Name: ${gv(personalDetails?.forename)} ${gv(
        personalDetails?.surname
      )}</li>
          <li>National Insurance Number: ${gv(
            personalDetails?.nationalInsuranceNumber
          )}</li>
          <li>Employment Dates:</li>
          From: ${fmtMonth(job.from)}<br/>
          To: ${fmtMonth(job.to)}
        </ul>
        <p>${gv(personalDetails?.forename)} stated that they were ${
        job.currentEmploymentStatus
      } as a ${job?.currentRole}.</p>
        <p>Please submit the reference using the link below:</p>
        ${
          job?.regulatedRole
            ? `<p><a href="${regulatedReferenceGoogleFormLink}" style="color:#081e56" target="_blank">Submit Reference</a></p>`
            : `<p><a href="${nonRegulatedReferenceGoogleFormLink}" style="color:#081e56" target="_blank">Submit Reference</a></p>`
        }
      `;
      return { subject, body, name, emailAddress };
    });

  // consent form download
  const handleDownloadConsentForm = async () => {
    const doc = (
      <ReferencesConsentForm
        printName={applicationDetails.declaration?.printName || ""}
        printNameDate={applicationDetails.declaration?.printNameDate || ""}
        printNameSignature={
          applicationDetails.declaration?.printNameSignature || ""
        }
      />
    );
    const blob = await pdf(doc).toBlob();
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `Reference Authorisation Form.pdf`;
    link.click();
  };

  // add reference to a job (PATCH_SECTION)
  const handleConfirmAdd = (ref) => {
    const idx = selectedJobForModal;
    if (idx == null) return;
    const job = workHistory[idx];
    if (!job) return;

    const updated = {
      ...job,
      references: [...(job.references || []), ref],
    };

    const next = [...workHistory];
    next[idx] = updated;

    dispatch({
      type: "PATCH_SECTION",
      payload: { section: "workHistory", patch: next },
    });

    setShowJobModal(false);
    setSelectedJobForModal(null);
  };

  // accountant ref attach
  const handleConfirmAddAccRef = (ref) => {
    dispatch({
      type: "PATCH_SECTION",
      payload: {
        section: "accountantDetails",
        patch: { references: ref },
      },
    });
    setShowJobModal(false);
  };

  // personal ref attach (per referee index)
  const handleConfirmAddPersonalRef = (ref, index) => {
    const cur = accountantDetails?.personalReferees || [];
    const target = { ...(cur[index] || {}) };
    const updated = {
      ...target,
      references: [...(target.references || []), ref],
    };
    const next = [...cur];
    next[index] = updated;

    dispatch({
      type: "PATCH_SECTION",
      payload: {
        section: "accountantDetails",
        patch: { personalReferees: next },
      },
    });
  };

  // educational ref attach (per referee index)
  const handleConfirmAddEducationalRef = (ref, index) => {
    const cur = accountantDetails?.educationalReferees || [];
    const target = { ...(cur[index] || {}) };
    const updated = {
      ...target,
      references: [...(target.references || []), ref],
    };
    const next = [...cur];
    next[index] = updated;

    dispatch({
      type: "PATCH_SECTION",
      payload: {
        section: "accountantDetails",
        patch: { educationalReferees: next },
      },
    });
    setShowJobModal(false);
    setSelectedEducationalRefIndex(null);
  };

  return (
    <div className="Main-Page">
      <Sidebar />
      <div className="Main-Container">
        <div className="AdviserContainer">
          <h2>
            {personalDetails?.forename} {personalDetails?.surname}
          </h2>
          {/* <PDFPreviewer
            PDF={<ReferenceAuthorisationForm data={formData} />}
          /> */}

          <WorkHistoryHeatmap />

          <div className="RadioButtonsHolder ">
            <div className="RadioButton-Container">
              <label className="Radio-Option">
                <input
                  type="radio"
                  value="Regulated & Non-Regulated References"
                  checked={filter === "Work History"}
                  onChange={() => setFilter("Work History")}
                />
                <span className="Custom-RadioButton"></span>
                <div className="Content">
                  Regulated & Non-Regulated References
                </div>
              </label>
              <label className="Radio-Option">
                <input
                  type="radio"
                  value="Accountant Reference"
                  checked={filter === "Accountant Reference"}
                  onChange={() => setFilter("Accountant Reference")}
                />
                <span className="Custom-RadioButton"></span>
                <div className="Content">Accountant Reference </div>
              </label>
              <label className="Radio-Option">
                <input
                  type="radio"
                  value="Personal Reference"
                  checked={filter === "Personal Reference"}
                  onChange={() => setFilter("Personal Reference")}
                />
                <span className="Custom-RadioButton"></span>
                <div className="Content">Personal References </div>
              </label>
              <label className="Radio-Option">
                <input
                  type="radio"
                  value="Educational Reference"
                  checked={filter === "Educational Reference"}
                  onChange={() => setFilter("Educational Reference")}
                />
                <span className="Custom-RadioButton"></span>
                <div className="Content">Educational References </div>
              </label>
              <label className="Radio-Option">
                <input
                  type="radio"
                  value="Trade Reference"
                  checked={filter === "Trade Reference"}
                  onChange={() => setFilter("Trade Reference")}
                />
                <span className="Custom-RadioButton"></span>
                <div className="Content">Trade References </div>
              </label>
            </div>
          </div>
          {/* <PDFViewer
          style={{
            width: "100%",
            height: "100%",
            position: "absolute",
            top: 0,
            left: 0,
            zIndex: 9999,
          }}
        >
          <ReferencesConsentForm
            data={formData}
          />
</PDFViewer> */}
          <div
            className="AdviserDashboardSmallRow"
            style={{ justifyContent: "center", gap: "50px" }}
          >
            <ButtonGreen
              buttonText="Save"
              className="Add-Button"
              onClick={() => {
                saveAdviserData(docId, state.data);
              }}
            />

            <ButtonYellow
              buttonText="Consent Form"
              onClick={() => {
                handleDownloadConsentForm();
              }}
            />
          </div>

          {filter === "Work History" && (
            <>
              <InputField
                title={`${personalDetails?.forename}'s National Insurance Number`}
                value={gv(personalDetails?.nationalInsuranceNumber) || ""}
                onChange={hc("personalDetails", "nationalInsuranceNumber")}
              />
              <div className="AdviserReferenceTableContainer">
                <table>
                  <thead>
                    <tr>
                      <th></th>
                      <th>Firm Name & Dates</th>
                      <th>Current Role & Status</th>
                      <th>Consent to Contact</th>
                      <th>View</th>
                    </tr>
                  </thead>
                  <tbody>
                    {workHistory.map((job, index) => (
                      <React.Fragment key={index}>
                        <tr>
                          <td>
                            <div className="Content">{index + 1}.</div>
                          </td>

                          <td>
                            <TableInputField
                              label="Firm Name"
                              value={job.nameOfFirm || ""}
                              onChange={(v) => {
                                const next = [...workHistory];
                                next[index] = {
                                  ...job,
                                  nameOfFirm: v?.target?.value ?? v,
                                };
                                dispatch({
                                  type: "PATCH_SECTION",
                                  payload: {
                                    section: "workHistory",
                                    patch: next,
                                  },
                                });
                              }}
                            />
                            <TableInputField
                              label="From"
                              specialType="month"
                              value={fmtMonth(job.from)}
                              onChange={(e) => {
                                const next = [...workHistory];
                                next[index] = { ...job, from: e.target.value };
                                dispatch({
                                  type: "PATCH_SECTION",
                                  payload: {
                                    section: "workHistory",
                                    patch: next,
                                  },
                                });
                              }}
                            />
                            <TableInputField
                              label="To"
                              specialType="month"
                              value={fmtMonth(job.to)}
                              onChange={(e) => {
                                const next = [...workHistory];
                                next[index] = { ...job, to: e.target.value };
                                dispatch({
                                  type: "PATCH_SECTION",
                                  payload: {
                                    section: "workHistory",
                                    patch: next,
                                  },
                                });
                              }}
                            />
                          </td>

                          <td>
                            <TableInputField
                              label="Role"
                              value={job.currentRole || ""}
                              onChange={(v) => {
                                const next = [...workHistory];
                                next[index] = {
                                  ...job,
                                  currentRole: v?.target?.value ?? v,
                                };
                                dispatch({
                                  type: "PATCH_SECTION",
                                  payload: {
                                    section: "workHistory",
                                    patch: next,
                                  },
                                });
                              }}
                            />
                            <TableInputField
                              label="Employment Status"
                              value={job.currentEmploymentStatus || ""}
                              onChange={(v) => {
                                const next = [...workHistory];
                                next[index] = {
                                  ...job,
                                  currentEmploymentStatus:
                                    v?.target?.value ?? v,
                                };
                                dispatch({
                                  type: "PATCH_SECTION",
                                  payload: {
                                    section: "workHistory",
                                    patch: next,
                                  },
                                });
                              }}
                            />
                            <TableInputField
                              label="Type of Reference"
                              value={
                                job.regulatedRole !== undefined
                                  ? job.regulatedRole
                                    ? "Regulated"
                                    : "Non-Regulated"
                                  : "Unknown"
                              }
                              onChange={() => {}}
                            />
                          </td>

                          <td>
                            <TableInputField
                              label="Consent"
                              value={
                                job.contactForReference === true ? "Yes" : "No"
                              }
                              onChange={() => {}}
                            />
                            <TableInputField
                              label="Reason"
                              isTextarea
                              value={job.reasonForNoReference || ""}
                              onChange={(v) => {
                                const next = [...workHistory];
                                next[index] = {
                                  ...job,
                                  reasonForNoReference: v?.target?.value ?? v,
                                };
                                dispatch({
                                  type: "PATCH_SECTION",
                                  payload: {
                                    section: "workHistory",
                                    patch: next,
                                  },
                                });
                              }}
                            />
                            <TableInputField
                              label="Email Address"
                              value={job.emailAddress || ""}
                              onChange={(v) => {
                                const next = [...workHistory];
                                next[index] = {
                                  ...job,
                                  emailAddress: v?.target?.value ?? v,
                                };
                                dispatch({
                                  type: "PATCH_SECTION",
                                  payload: {
                                    section: "workHistory",
                                    patch: next,
                                  },
                                });
                              }}
                            />
                          </td>

                          <td style={{ width: 50 }}>
                            {job.contactForReference === true ? (
                              <FontAwesomeIcon
                                icon={
                                  job?.references
                                    ? expandedRows.includes(index)
                                      ? faChevronCircleUp
                                      : faChevronCircleDown
                                    : faPlus
                                }
                                className="notes-icon"
                                onClick={() => {
                                  if (job?.references) toggleRow(index);
                                  else {
                                    setSelectedJobForModal(index);
                                    setShowJobModal(true);
                                  }
                                }}
                              />
                            ) : null}
                          </td>
                        </tr>

                        {expandedRows.includes(index) && (
                          <>
                            <tr></tr>
                            <tr style={{ borderTop: "1px solid #f4f4f9" }}>
                              <td></td>
                              <td colSpan={5}>
                                <div>
                                  <div className="Content-Bold-Underline ">
                                    Reference Details
                                    <div
                                      style={{
                                        display: "flex",
                                        justifyContent: "flex-end",
                                        paddingRight: "12px",
                                      }}
                                    >
                                      <FontAwesomeIcon
                                        icon={faTrash}
                                        className="notes-icon"
                                        title="Delete reference"
                                        onClick={() =>
                                          handleDeleteWorkRef(index, 0)
                                        }
                                      />
                                    </div>
                                  </div>

                                  {job?.references?.map((reference, index) => {
                                    const isRegulated =
                                      job?.regulatedRole === true;

                                    return (
                                      <React.Fragment key={index}>
                                        {isRegulated ? (
                                          <div className="ReferenceRow">
                                            <TableInputField
                                              label="Name of the company providing reference"
                                              value={reference.companyName}
                                              onChange={() => {}}
                                            />
                                            <TableInputField
                                              label="Contact details of the company providing
                                        reference"
                                              value={
                                                reference.contactDetails || ""
                                              }
                                              onChange={() => {}}
                                            />
                                            <TableInputField
                                              label="Firm reference number of firm providing reference"
                                              value={
                                                reference.firmreferenceNumber ||
                                                ""
                                              }
                                              onChange={() => {}}
                                            />
                                            <TableInputField
                                              label="Individual’s name (i.e. the subject of
                                        the reference)"
                                              value={
                                                reference.adviserName || ""
                                              }
                                            />
                                            <TableInputField
                                              label="Submitted On"
                                              value={
                                                dayjs(
                                                  reference.submittedAt
                                                ).format("DD/MM/YYYY ") || ""
                                              }
                                              onChange={() => {}}
                                            />
                                            <TableInputField
                                              label="Date that the reference was sent"
                                              value={
                                                dayjs(
                                                  reference.referenceSentDate
                                                ).format("DD/MM/YYYY") || ""
                                              }
                                              onChange={() => {}}
                                            />
                                            <TableInputField
                                              label="Dates of Employment"
                                              value={
                                                dayjs(
                                                  reference.dateOfEmployment
                                                ).format("DD/MM/YYYY") || ""
                                              }
                                              onChange={() => {}}
                                            />
                                            <TableInputField
                                              label="Reason for ending employment"
                                              value={
                                                reference.reasonForLeaving || ""
                                              }
                                            />
                                            <TableInputField
                                              label="Complaints  (If yes, were they upheld?)"
                                              value={reference.complaints || ""}
                                              onChange={() => {}}
                                            />
                                            <TableInputField
                                              label="Amount of outstanding debt (If there is any, is there a repayment schedule in place?)"
                                              value={
                                                reference.outstandingDebts || ""
                                              }
                                              onChange={() => {}}
                                            />
                                            <div className="Content-SemiBold">
                                              Training and Competency:
                                            </div>
                                            <TableInputField
                                              label="Competent adviser status"
                                              value={
                                                reference.competentAdviserStatus ||
                                                ""
                                              }
                                              onChange={() => {}}
                                            />
                                            <TableInputField
                                              label="Did the Adviser meet the requirements of your Training and Competency (T&C) Scheme?"
                                              value={
                                                reference.trainingAndCompetencyStatus ||
                                                ""
                                              }
                                              onChange={() => {}}
                                            />
                                            <TableInputField
                                              label="If you answered 'No' to the above question, please provide the relevant information below. Otherwise, enter 'NA'."
                                              value={
                                                reference.trainingAndCompetencyDetails ||
                                                ""
                                              }
                                              onChange={() => {}}
                                            />
                                            <div className="Content-SemiBold">
                                              Standard of Advice (if yes, please
                                              comment further):
                                            </div>

                                            <TableInputField
                                              label="Are you aware of the individual being removed from a lender’s mortgage panel?"
                                              value={
                                                reference.removedFromMortgagePanel ||
                                                ""
                                              }
                                              onChange={() => {}}
                                            />
                                            <TableInputField
                                              label="Was there any evidence of failing to give the best advice?"
                                              value={reference.bestAdvice || ""}
                                              onChange={() => {}}
                                            />
                                            <div className="Content-SemiBold">
                                              The answers to Questions A to F
                                              cover the period beginning six
                                              years before the date of your
                                              request for a reference and ending
                                              on the date of this reference. :
                                            </div>
                                            <TableInputField
                                              label={
                                                <>
                                                  Question A<br />
                                                  Has the individual
                                                  <br />
                                                  (1) Performed a certification
                                                  function for your firm; or
                                                  <br />
                                                  (2) Been an approved person
                                                  for your firm
                                                </>
                                              }
                                              value={
                                                reference.certificationOrApprovedPersonStatus ||
                                                ""
                                              }
                                              onChange={() => {}}
                                            />
                                            <TableInputField
                                              label="Please provide any other relevant details below in relation to Question A"
                                              value={
                                                reference.certificationOrApprovedPersonDetails ||
                                                "Not Provided"
                                              }
                                              onChange={() => {}}
                                            />
                                            <TableInputField
                                              label={
                                                <>
                                                  Question B<br />
                                                  Has the individual performed
                                                  one or more of the following
                                                  roles in relation to your
                                                  firm:
                                                  <br />
                                                  (1) Notified non-executive
                                                  director:
                                                  <br />
                                                  (2) Credit union non-executive
                                                  director; or
                                                  <br />
                                                  (3) Key function holder (other
                                                  than a controlled function)
                                                </>
                                              }
                                              value={
                                                reference.performedKeyOrNEDRoles ||
                                                ""
                                              }
                                              onChange={() => {}}
                                            />
                                            <TableInputField
                                              label="Please provide any other relevant details below in relation to Question B"
                                              value={
                                                reference.performedKeyOrNEDRolesDetails ||
                                                "Not Provided"
                                              }
                                              onChange={() => {}}
                                            />
                                            <TableInputField
                                              label={
                                                <>
                                                  Question C<br />
                                                  If you have answered “yes” to
                                                  either Question A or B above,
                                                  set out the details of each
                                                  position held below,
                                                  including:
                                                  <br />
                                                  (1) What the controlled
                                                  function, certification
                                                  function or key function
                                                  holder role is or was;
                                                  <br />
                                                  (2) (In the case of a
                                                  controlled function) whether
                                                  the approval is or was subject
                                                  to a condition, suspension,
                                                  limitation, restriction or any
                                                  time limit;
                                                  <br />
                                                  (3) Whether any potential FCA
                                                  governing function is or was
                                                  included in a PRA controlled
                                                  function; and
                                                  <br />
                                                  (4) The dates during which the
                                                  individual held the position.
                                                </>
                                              }
                                              value={
                                                reference.positionDetailsSummary ||
                                                ""
                                              }
                                              onChange={() => {}}
                                            />
                                            <TableInputField
                                              label={
                                                <>
                                                  Question D<br />
                                                  Has the individual performed a
                                                  role for your firm other than
                                                  the roles referred to in
                                                  Questions A and B above?
                                                </>
                                              }
                                              value={
                                                reference.performedOtherRoles ||
                                                ""
                                              }
                                              onChange={() => {}}
                                            />
                                            <TableInputField
                                              label="If “Yes” please provide summary details of the other roles (e.g job title, department and business unit, below)"
                                              value={
                                                reference.performedOtherRolesDetails ||
                                                "Not Provided"
                                              }
                                              onChange={() => {}}
                                            />
                                            <TableInputField
                                              label={
                                                <>
                                                  Question E<br />
                                                  Have you concluded that the
                                                  individual was not Fit and
                                                  Proper to perform a function?
                                                  <br />
                                                  If “yes” and associated
                                                  disciplinary action was taken
                                                  as a result, please refer to
                                                  Question F below.
                                                </>
                                              }
                                              value={
                                                reference.isNotFitAndProper ||
                                                ""
                                              }
                                              onChange={() => {}}
                                            />
                                            <TableInputField
                                              label="If “yes” and no associated disciplinary action was taken as a result, please set out below the facts which led to your conclusion"
                                              value={
                                                reference.isNotFitAndProperDetails ||
                                                "Not Provided"
                                              }
                                              onChange={() => {}}
                                            />
                                            <TableInputField
                                              label={
                                                <>
                                                  Question F<br />
                                                  Have you taken disciplinary
                                                  action against the individual
                                                  that:
                                                  <br />
                                                  (1) Relates to an action,
                                                  failure to act, or
                                                  circumstances, that amount to
                                                  a breach of any individual
                                                  conduct requirements that:
                                                  <br />
                                                  (a) Apply or applied to the
                                                  individual; or
                                                  <br />
                                                  (b) If the individual is or
                                                  was a key function holder, a
                                                  notified non-executive
                                                  director or a credit union
                                                  non-executive director for
                                                  your firm)
                                                  <br />
                                                  the individual is or was
                                                  required to observe under PRA
                                                  rules (including if
                                                  applicable, PRA rules in force
                                                  before 7 March 2016); or
                                                  <br />
                                                  (2) Relates to the individual
                                                  not being fit and proper to
                                                  perform a function?
                                                </>
                                              }
                                              value={
                                                reference.disciplinaryActionTaken ||
                                                ""
                                              }
                                              onChange={() => {}}
                                            />
                                            <TableInputField
                                              label={
                                                <>
                                                  If “yes”, please can you
                                                  provide below a description of
                                                  the breaches (including dates
                                                  of when they<br></br>occurred)
                                                  and the basis for, and outcome
                                                  of, the subsequent
                                                  disciplinary action.
                                                </>
                                              }
                                              value={
                                                reference.disciplinaryActionDetails ||
                                                "Not Provided"
                                              }
                                              onChange={() => {}}
                                            />
                                            <TableInputField
                                              label={
                                                <>
                                                  Question G<br />
                                                  Are you aware of any other
                                                  information that you
                                                  reasonably consider to be
                                                  relevant
                                                  <br></br>to your assessment of
                                                  whether the individual is fit
                                                  and proper?
                                                  <br></br>This disclosure is
                                                  made on the basis that you
                                                  will only disclose something
                                                  that:
                                                  <br />
                                                  (1) Occurred or existed:
                                                  <br />
                                                  (a) In the six years before
                                                  this request for a reference;
                                                  or
                                                  <br />
                                                  (b) Between the date of the
                                                  request for the reference and
                                                  the date of this reference; or
                                                  <br />
                                                  (2) is serious misconduct.
                                                </>
                                              }
                                              value={
                                                reference.otherFitAndProperConcerns ||
                                                ""
                                              }
                                              onChange={() => {}}
                                            />
                                            <TableInputField
                                              label="If “Yes” please provide the relevant information below "
                                              value={
                                                reference.otherFitAndProperDetails ||
                                                "Not Provided"
                                              }
                                              onChange={() => {}}
                                            />
                                            <TableInputField
                                              label="Additional Information"
                                              isTextarea
                                              value={
                                                reference.additionalInformation ||
                                                ""
                                              }
                                              onChange={() => {}}
                                            />
                                          </div>
                                        ) : (
                                          <>
                                            <TableInputField
                                              label="Name of the company providing reference"
                                              value={reference.companyName}
                                              onChange={() => {}}
                                            />
                                            <TableInputField
                                              label="Contact details of the company providing
                                        reference"
                                              value={
                                                reference.contactDetails || ""
                                              }
                                              onChange={() => {}}
                                            />
                                            <TableInputField
                                              label="Individual’s name (i.e. the subject of
                                        the reference)"
                                              value={
                                                reference.adviserName || ""
                                              }
                                              onChange={() => {}}
                                            />
                                            <TableInputField
                                              label="Submitted On"
                                              value={
                                                dayjs(
                                                  reference.submittedAt
                                                ).format("DD/MM/YYYY ") || ""
                                              }
                                              onChange={() => {}}
                                            />
                                            <TableInputField
                                              label="1. During what period did you work with
                                        the individual? (Start Date)"
                                              value={
                                                dayjs(
                                                  reference.startDate
                                                ).format("DD/MM/YYYY") || ""
                                              }
                                              onChange={() => {}}
                                            />
                                            <TableInputField
                                              label="2. During what period did you work with
                                        the individual? (End Date)"
                                              value={
                                                dayjs(reference.endDate).format(
                                                  "DD/MM/YYYY"
                                                ) || ""
                                              }
                                              onChange={() => {}}
                                            />
                                            <TableInputField
                                              label="3. What role did the individual perform?"
                                              value={reference.role || ""}
                                              onChange={() => {}}
                                            />
                                            <TableInputField
                                              label="4. What daily activities are required
                                        within their role?"
                                              value={
                                                reference.dailyActivities || ""
                                              }
                                              onChange={() => {}}
                                            />
                                            <TableInputField
                                              label="5. In your opinion, do you consider the
                                        individual able to demonstrate good
                                        honesty and integrity?"
                                              value={
                                                reference.honestyAndIntegrity ||
                                                ""
                                              }
                                              onChange={() => {}}
                                            />
                                            <TableInputField
                                              label="6. Was there ever any complaints or
                                        warnings against the individual?"
                                              value={reference.complaints || ""}
                                              onChange={() => {}}
                                            />
                                            <TableInputField
                                              label="7. Was the individual’s contract
                                        terminated due to poor conduct?"
                                              value={
                                                reference.contractTerminated ||
                                                ""
                                              }
                                              onChange={() => {}}
                                            />
                                            <TableInputField
                                              label="If yes to questions 6 or 7, please
                                        provide further detail below"
                                              value={reference.details || ""}
                                              onChange={() => {}}
                                            />
                                          </>
                                        )}
                                      </React.Fragment>
                                    );
                                  })}
                                </div>
                              </td>
                            </tr>
                          </>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
              <div
                className="AdviserDashboardSmallRow"
                style={{ justifyContent: "center", gap: "50px" }}
              >
                {/* <ButtonGreen
                  buttonText="Save"
                  className="Add-Button"
                  onClick={() => {
                    // handleSave();
                  }}
                /> */}
                {workHistory?.some(
                  (job) =>
                    job?.contactForReference === true &&
                    (!Array.isArray(job?.references) ||
                      job.references.length === 0)
                ) && (
                  <ButtonGreen
                    buttonText="Preview Emails"
                    onClick={() => setShowEmailModal(true)}
                  />
                )}
              </div>
            </>
          )}

          {filter === "Accountant Reference" && (
            <>
              <div className="AdviserDashboardColumn ParentContainer">
                <div className="SubTitle">Accountant Details</div>
                <div className="AdviserDashboardRow">
                  <div className="AdviserDashboardColumn">
                    <InputField
                      title="Forename"
                      value={gv(accountantDetails?.initials) || ""}
                      onChange={hc("accountantDetails", "initials")}
                    />
                    <InputField
                      title="Surname"
                      value={gv(accountantDetails?.surname) || ""}
                      onChange={hc("accountantDetails", "surname")}
                    />
                    <InputField
                      title="From"
                      specialType="month"
                      value={fmtMonth(accountantDetails?.from)}
                      onChange={hc("accountantDetails", "from")}
                    />
                  </div>
                  <div className="AdviserDashboardColumn">
                    <InputField
                      title="Telephone Number"
                      value={gv(accountantDetails?.telephone) || ""}
                      onChange={hc("accountantDetails", "telephone")}
                    />
                    <InputField
                      title="Email Address"
                      value={gv(accountantDetails?.emailAddress) || ""}
                      onChange={hc("accountantDetails", "emailAddress")}
                    />
                  </div>
                </div>
              </div>

              <div className="AdviserDashboardColumn ParentContainer">
                <div className="AdviserDashboardColumn">
                  <div className="AdviserDashboardRow">
                    <div className="Content-Bold-Underline">
                      Reference Details:
                    </div>
                    {accountantDetails?.references ? (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "flex-end",
                          paddingRight: "12px",
                        }}
                      >
                        <FontAwesomeIcon
                          icon={faTrash}
                          className="notes-icon"
                          title="Delete reference"
                          onClick={() => handleDeleteAccountantRef(0)}
                        />
                      </div>
                    ) : (
                      <FontAwesomeIcon
                        icon={faPlusCircle}
                        style={{ alignSelf: "center" }}
                        className="view-icon"
                        onClick={() => setShowJobModal(true)}
                      />
                    )}
                  </div>

                  {accountantDetails?.references && (
                    <div className="AdviserDashboardRow">
                      <div className="AdviserDashboardColumn">
                        <InputField
                          title="Individual’s name"
                          value={
                            gv(accountantDetails?.references?.adviserName) || ""
                          }
                          onChange={hc(
                            "accountantDetails",
                            "references.adviserName"
                          )}
                        />
                        <InputField
                          title="How long have you been an accountant for?"
                          value={
                            gv(
                              accountantDetails?.references?.yearsAsAccountant
                            ) || ""
                          }
                          onChange={hc(
                            "accountantDetails",
                            "references.yearsAsAccountant"
                          )}
                        />
                        <InputField
                          title="How long working with the individual?"
                          value={
                            gv(
                              accountantDetails?.references
                                ?.yearsWorkingWithIndividual
                            ) || ""
                          }
                          onChange={hc(
                            "accountantDetails",
                            "references.yearsWorkingWithIndividual"
                          )}
                        />
                        <InputField
                          title="Submitted By"
                          value={
                            gv(accountantDetails?.references?.submittedBy) || ""
                          }
                          onChange={hc(
                            "accountantDetails",
                            "references.submittedBy"
                          )}
                        />
                      </div>
                      <div className="AdviserDashboardColumn">
                        <InputField
                          title="Can you confirm what the individual was doing between the period mentioned in the mail?"
                          value={
                            gv(
                              accountantDetails?.references?.periodConfirmation
                            ) || ""
                          }
                          onChange={hc(
                            "accountantDetails",
                            "references.periodConfirmation"
                          )}
                        />
                        <InputField
                          title="Additional Information"
                          isTextarea
                          value={
                            gv(
                              accountantDetails?.references
                                ?.additionalInformation
                            ) || ""
                          }
                          onChange={hc(
                            "accountantDetails",
                            "references.additionalInformation"
                          )}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div
                className="AdviserDashboardSmallRow"
                style={{ justifyContent: "center", gap: 50 }}
              >
                {!accountantDetails?.references && (
                  <ButtonGreen
                    buttonText="Preview Emails"
                    onClick={() => setShowEmailModal(true)}
                  />
                )}
              </div>
            </>
          )}
          {filter === "Personal Reference" && (
            <>
              <div className="AdviserDashboardColumn ParentContainer">
                <div className="SubTitle">Personal Referees</div>
                <div className="AdviserDashboardRow">
                  {(accountantDetails?.personalReferees || []).map(
                    (referee, index) => (
                      <div className="AdviserDashboardColumn" key={index}>
                        <div className="Content">
                          Personal Referee {index + 1}
                        </div>

                        <InputField
                          title="Referee Name"
                          value={referee.refereeName || ""}
                          onChange={hc(
                            "accountantDetails.personalReferees",
                            "refereeName",
                            index
                          )}
                        />
                        <InputField
                          title="Referee Address"
                          isTextarea
                          value={referee.refereeAddress || ""}
                          onChange={hc(
                            "accountantDetails.personalReferees",
                            "refereeAddress",
                            index
                          )}
                        />
                        <InputField
                          title="Office Telephone"
                          value={referee.refereePhoneNumber || ""}
                          onChange={hc(
                            "accountantDetails.personalReferees",
                            "refereePhoneNumber",
                            index
                          )}
                        />
                        <InputField
                          title="Referee Email Address"
                          value={referee.refereeEmailAddress || ""}
                          onChange={hc(
                            "accountantDetails.personalReferees",
                            "refereeEmailAddress",
                            index
                          )}
                        />
                        <InputField
                          title="Relationship to Referee"
                          value={referee.relationshipToReferee || ""}
                          onChange={hc(
                            "accountantDetails.personalReferees",
                            "relationshipToReferee",
                            index
                          )}
                        />

                        {/* Reference Details UI stays as-is */}
                      </div>
                    )
                  )}
                </div>
              </div>

              <div
                className="AdviserDashboardSmallRow"
                style={{ justifyContent: "center", gap: "50px" }}
              >
                {(accountantDetails?.personalReferees || []).some(
                  (ref) =>
                    !Array.isArray(ref?.references) ||
                    ref.references.length === 0
                ) && (
                  <ButtonGreen
                    buttonText="Preview Emails"
                    onClick={() => setShowEmailModal(true)}
                  />
                )}
              </div>
            </>
          )}

          {filter === "Educational Reference" && (
            <>
              <div className="AdviserDashboardColumn ParentContainer">
                {(accountantDetails?.educationalReferees || []).map(
                  (referee, index) => (
                    <>
                      <div className="AdviserDashboardRow" key={index}>
                        <div className="AdviserDashboardColumn">
                          <div className="SubTitle">Educational Reference</div>
                          <InputField
                            title="Forename"
                            value={personalDetails?.forename || ""}
                            onChange={hc("personalDetails", "forename")}
                          />
                          <InputField
                            title="Surname"
                            value={personalDetails?.surname || ""}
                            onChange={hc("personalDetails", "surname")}
                          />
                          <InputField
                            title="Educational Institute"
                            value={referee.educationalInstitute || ""}
                            onChange={hc(
                              "accountantDetails.educationalReferees",
                              "educationalInstitute",
                              index
                            )}
                          />
                          <InputField
                            title="Recipient Name"
                            value={referee.recipientName || ""}
                            onChange={hc(
                              "accountantDetails.educationalReferees",
                              "recipientName",
                              index
                            )}
                          />
                          <InputField
                            title="Recipient Email Address"
                            value={referee.recipientEmail || ""}
                            onChange={hc(
                              "accountantDetails.educationalReferees",
                              "recipientEmail",
                              index
                            )}
                          />
                        </div>

                        <div className="AdviserDashboardColumn">
                          <div className="Content-Bold-Underline">
                            Reference Details
                          </div>
                          {!referee?.references ? (
                            <FontAwesomeIcon
                              icon={faPlusCircle}
                              className="view-icon"
                              onClick={() => {
                                setSelectedReferenceForModal(index);
                                setSelectedEducationalRefIndex(index);
                                setShowJobModal(true);
                              }}
                            />
                          ) : (
                            <>
                              <InputField
                                title="Individual’s name (i.e. the subject of the reference)"
                                value={
                                  referee.references?.[0]?.adviserName || ""
                                }
                                onChange={() => {}}
                              />
                              <InputField
                                title="During which period(s) was the individual enrolled at the institute mentioned in the email? "
                                value={
                                  referee.references?.[0]?.yearsAsAccountant ||
                                  ""
                                }
                                onChange={() => {}}
                              />
                              <InputField
                                title="In your opinion, did the individual demonstrate honesty and integrity during his studies?"
                                value={
                                  referee.references?.[0]
                                    ?.yearsWorkingWithIndividual || ""
                                }
                                onChange={() => {}}
                              />

                              <InputField
                                title="Was the student ever involved in any disciplinary actions or matters of concern?

"
                                value={
                                  referee.references?.[0]?.submittedBy || ""
                                }
                                onChange={() => {}}
                              />

                              <InputField
                                title="How would you describe the student’s overall conduct and character?
"
                                value={
                                  referee.references?.[0]?.submittedBy || ""
                                }
                                onChange={() => {}}
                              />
                              <InputField
                                title="Additional Information"
                                isTextarea
                                value={
                                  referee.references?.[0]
                                    ?.additionalInformation || ""
                                }
                                onChange={() => {}}
                              />
                            </>
                          )}
                        </div>
                      </div>
                    </>
                  )
                )}
              </div>
              <div
                className="AdviserDashboardSmallRow"
                style={{ justifyContent: "center", gap: "30px" }}
              >
                {accountantDetails?.educationalReferees?.some(
                  (ref) =>
                    !Array.isArray(ref?.references) ||
                    ref.references.length === 0
                ) && (
                  <ButtonGreen
                    buttonText="Preview Emails"
                    onClick={() => setShowEmailModal(true)}
                  />
                )}
              </div>
            </>
          )}

          {filter === "Trade Reference" && (
            <>
              <div className="AdviserDashboardColumn ParentContainer">
                <div className="SubTitle">Trade Referees</div>
                <div className="AdviserDashboardRow">
                  {accountantDetails?.tradeReferees?.map((referee, index) => (
                    <div className="AdviserDashboardColumn" key={index}>
                      <div className="Content">Trade Referee {index + 1}</div>

                      <InputField
                        title="Trade Name"
                        value={referee.tradeName || ""}
                        onChange={hc(
                          "accountantDetails.tradeReferees",
                          "tradeName",
                          index
                        )}
                      />
                      <InputField
                        title="Company Name"
                        value={referee.companyName || ""}
                        onChange={hc(
                          "accountantDetails.tradeReferees",
                          "companyName",
                          index
                        )}
                      />
                      <InputField
                        title="Trade Address"
                        isTextarea
                        value={referee.tradeAddress || ""}
                        onChange={hc(
                          "accountantDetails.tradeReferees",
                          "tradeAddress",
                          index
                        )}
                      />
                      <InputField
                        title="Trade Email Address"
                        value={referee.tradeEmailAddress || ""}
                        onChange={hc(
                          "accountantDetails.tradeReferees",
                          "tradeEmailAddress",
                          index
                        )}
                      />
                      <InputField
                        title="Office Telephone"
                        value={referee.refereePhoneNumber || ""}
                        onChange={hc(
                          "accountantDetails.tradeReferees",
                          "refereePhoneNumber",
                          index
                        )}
                      />
                      <InputField
                        title="Dates of Trading Activities"
                        isTextarea
                        value={referee.datesOfTradingActivities || ""}
                        onChange={hc(
                          "accountantDetails.tradeReferees",
                          "datesOfTradingActivities",
                          index
                        )}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {showJobModal && filter === "Work History" && (
            <div className="modal-overlay">
              <div className="modal-content">
                {(() => {
                  const selectedJob = workHistory?.[selectedJobForModal];
                  const isRegulated = selectedJob?.regulatedRole === true;
                  const referenceList = isRegulated
                    ? regulatedReferences
                    : nonRegulatedReferences;

                  return (
                    <>
                      <h3>
                        {isRegulated
                          ? "Regulated References"
                          : "Non Regulated References"}
                      </h3>
                      <CloseIcon
                        className="close-icon"
                        onClick={() => setShowJobModal(false)}
                      />
                      {loadingReferences ? (
                        <p>Loading...</p>
                      ) : (
                        <table>
                          <thead>
                            <tr>
                              <th>Adviser Name</th>
                              <th>Company Name</th>
                              <th>Submitted At</th>
                              <th>Add</th>
                            </tr>
                          </thead>
                          <tbody>
                            {referenceList.map((ref) => (
                              <tr key={ref.id}>
                                <td>
                                  <div className="Content">
                                    {ref.adviserName}
                                  </div>
                                </td>
                                <td>
                                  <div className="Content">
                                    {ref.companyName}
                                  </div>
                                </td>
                                <td>
                                  <div className="Content">
                                    {ref.submittedAt
                                      ? dayjs(ref.submittedAt).format(
                                          "DD/MM/YYYY "
                                        )
                                      : ""}
                                  </div>
                                </td>
                                <td>
                                  <FontAwesomeIcon
                                    icon={faPlusCircle}
                                    className="view-icon"
                                    onClick={() => handleConfirmAdd(ref)}
                                  />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          )}
          {showJobModal && filter === "Accountant Reference" && (
            <div className="modal-overlay">
              <div className="modal-content">
                {(() => {
                  const referenceList = accountantReferences;

                  return (
                    <>
                      <h3>Accountant References</h3>
                      <CloseIcon
                        className="close-icon"
                        onClick={() => setShowJobModal(false)}
                      />
                      {loadingReferences ? (
                        <p>Loading...</p>
                      ) : (
                        <table>
                          <thead>
                            <tr>
                              <th>Adviser Name</th>
                              <th>Submitted By</th>
                              <th>Submitted At</th>
                              <th>Add</th>
                            </tr>
                          </thead>
                          <tbody>
                            {referenceList.map((ref) => (
                              <tr key={ref.id}>
                                <td>
                                  <div className="Content">
                                    {ref.adviserName}
                                  </div>
                                </td>
                                <td>
                                  <div className="Content">
                                    {ref.submittedBy}
                                  </div>
                                </td>
                                <td>
                                  <div className="Content">
                                    {ref.submittedAt
                                      ? dayjs(ref.submittedAt).format(
                                          "DD/MM/YYYY "
                                        )
                                      : ""}
                                  </div>
                                </td>
                                <td>
                                  <FontAwesomeIcon
                                    icon={faPlusCircle}
                                    className="view-icon"
                                    onClick={() => {
                                      setShowJobModal(false);
                                      handleConfirmAddAccRef(ref);
                                    }}
                                  />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          )}
          {showJobModal && filter === "Personal Reference" && (
            <div className="modal-overlay">
              <div className="modal-content">
                {(() => {
                  const referenceList = personalReferences;

                  return (
                    <>
                      <h3>Personal References</h3>
                      <CloseIcon
                        className="close-icon"
                        onClick={() => setShowJobModal(false)}
                      />
                      {loadingReferences ? (
                        <p>Loading...</p>
                      ) : (
                        <table>
                          <thead>
                            <tr>
                              <th>Adviser Name</th>
                              <th>Submitted By</th>
                              <th>Submitted At</th>
                              <th>Add</th>
                            </tr>
                          </thead>

                          <tbody>
                            {referenceList.map((ref) => (
                              <tr key={ref.id}>
                                <td>
                                  <div className="Content">
                                    {ref.adviserName}
                                  </div>
                                </td>
                                <td>
                                  <div className="Content">
                                    {ref.submittedBy}
                                  </div>
                                </td>
                                <td>
                                  <div className="Content">
                                    {ref.submittedAt
                                      ? dayjs(ref.submittedAt).format(
                                          "DD/MM/YYYY "
                                        )
                                      : ""}
                                  </div>
                                </td>
                                <td>
                                  <FontAwesomeIcon
                                    icon={faPlusCircle}
                                    className="view-icon"
                                    onClick={() => {
                                      setShowJobModal(false);
                                      handleConfirmAddPersonalRef(
                                        ref,
                                        selectedPersonalRefIndex
                                      ); // use `ref` from map
                                    }}
                                  />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          )}
          {showJobModal && filter === "Educational Reference" && (
            <div className="modal-overlay">
              <div className="modal-content">
                {(() => {
                  const referenceList = educationalReferences;

                  return (
                    <>
                      <h3>Educational References</h3>
                      <CloseIcon
                        className="close-icon"
                        onClick={() => setShowJobModal(false)}
                      />
                      {loadingReferences ? (
                        <p>Loading...</p>
                      ) : (
                        <table>
                          <thead>
                            <tr>
                              <th>Adviser Name</th>
                              <th>Submitted By</th>
                              <th>Submitted At</th>
                              <th>Add</th>
                            </tr>
                          </thead>
                          <tbody>
                            {referenceList.map((ref) => (
                              <tr key={ref.id}>
                                <td>
                                  <div className="Content">
                                    {ref.adviserName}
                                  </div>
                                </td>
                                <td>
                                  <div className="Content">
                                    {ref.submittedBy}
                                  </div>
                                </td>
                                <td>
                                  <div className="Content">
                                    {ref.submittedAt
                                      ? dayjs(ref.submittedAt).format(
                                          "DD/MM/YYYY "
                                        )
                                      : ""}
                                  </div>
                                </td>
                                <td>
                                  <FontAwesomeIcon
                                    icon={faPlusCircle}
                                    className="view-icon"
                                    onClick={() => {
                                      setShowJobModal(false);
                                      handleConfirmAddEducationalRef(
                                        ref,
                                        selectedEducationalRefIndex
                                      );
                                    }}
                                  />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          )}
        </div>
        {showEmailModal && filter === "Work History" && (
          <EmailPreviewModal
            previews={workHistoryEmailPreviews}
            endpoint="/sendWorkHistoryReferencesRequests"
            onClose={() => {
              setEmailPreview("");
              setShowEmailModal(false);
            }}
            onUpdate={handleUpdateEmail}
          />
        )}
        {showEmailModal && filter === "Accountant Reference" && (
          <EmailPreviewModal
            previews={accountantReferenceEmailPreviews}
            endpoint="/sendaccountantReferenceRequest"
            onClose={() => {
              setEmailPreview("");
              setShowEmailModal(false);
            }}
            onUpdate={handleUpdateEmail}
          />
        )}

        {showEmailModal && filter === "Personal Reference" && (
          <EmailPreviewModal
            previews={personalReferenceEmailPreviews}
            endpoint="/sendpersonalReferenceRequest"
            onClose={() => {
              setEmailPreview("");
              setShowEmailModal(false);
            }}
            onUpdate={handleUpdateEmail}
          />
        )}
        {showEmailModal && filter === "Educational Reference" && (
          <EmailPreviewModal
            previews={educationalReferenceEmailPreviews}
            endpoint="/sendeducationalReferenceRequest"
            onClose={() => {
              setEmailPreview("");
              setShowEmailModal(false);
            }}
            onUpdate={handleUpdateEmail}
          />
        )}
      </div>
    </div>
  );
};

export default GetAdviserReferences;
